import { activityTrackerFor, createApiClient, type ApiClient } from '@brush/api-client';
import { ADMIN_SCREENS } from './activity/screens';
import type { AdminView } from './nav';
import { useAccessToken, useCurrentAccount, useSignOut } from '@brush/auth';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { apiBaseUrl, apiScope } from './authConfig';

const ApiContext = createContext<ApiClient | null>(null);

export interface Session {
  displayName: string | null;
  /** null when auth is disabled locally — there is nothing to sign out of. */
  signOut: (() => void) | null;
}

const SessionContext = createContext<Session>({ displayName: null, signOut: null });

export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) throw new Error('useApi must be used within an ApiProvider.');
  return client;
}

export function useSession(): Session {
  return useContext(SessionContext);
}

const DEV_SESSION: Session = { displayName: 'Development', signOut: null };

/** Local dev: no MSAL. The dev-bypass API authenticates every request. */
export function DevApiProvider({ children }: { children: ReactNode }) {
  const client = useMemo(
    () => createApiClient({ baseUrl: apiBaseUrl, getAccessToken: () => Promise.resolve('dev') }),
    [],
  );
  return (
    <ApiContext.Provider value={client}>
      <SessionContext.Provider value={DEV_SESSION}>{children}</SessionContext.Provider>
    </ApiContext.Provider>
  );
}

/** Production: real access token from MSAL. Must render inside <AuthProvider> and <RequireAuth>. */
export function MsalApiProvider({ children }: { children: ReactNode }) {
  const getAccessToken = useAccessToken(apiScope);
  const account = useCurrentAccount();
  const signOut = useSignOut();

  const client = useMemo(
    () => createApiClient({ baseUrl: apiBaseUrl, getAccessToken }),
    [getAccessToken],
  );
  const session = useMemo<Session>(
    () => ({ displayName: account?.name ?? null, signOut }),
    [account?.name, signOut],
  );

  return (
    <ApiContext.Provider value={client}>
      <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
    </ApiContext.Provider>
  );
}

/**
 * Reports this app's session once it is in use — once per app session, which the tracker keeps in
 * sessionStorage, so a reload is not a second session (ADR 0012 Decision 13). The console's own code: the
 * server refuses the other app's session from this app's token.
 */
export function useSessionStarted(ready: boolean) {
  const api = useApi();
  useEffect(() => {
    if (ready) activityTrackerFor(api).sessionStarted('console.session_started');
  }, [api, ready]);
}

/**
 * Reports an admin-web area on entry (ADR 0012 step 18) — once per area per app session, never retried.
 * Null reports nothing, which is what a visitor refused at the access check gets. No room: every area
 * is platform-wide.
 */
export function useScreenViewed(screen: AdminView | null) {
  const api = useApi();
  useEffect(() => {
    if (screen !== null) activityTrackerFor(api).viewed(ADMIN_SCREENS[screen], null);
  }, [api, screen]);
}
